/**
 * English: Files
 *
 * What this file covers: Uploading files and previewing them (documents, images, audio, video).
 *
 * Tone: Short functional labels, like a file manager.
 *
 * English: English is the reference: every key is added here first, in South African English (organise, colour,
 * cancelled). Every other language falls back to this text for any key it does not have yet.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const files: Catalog = {
  'upload.prompt': 'Drop a file here, or click to browse',
  'upload.library': 'Choose from library',
  'upload.library.title': 'Media library',
  'upload.library.search': 'Search by filename',
  'upload.library.empty': 'Nothing here yet. Upload a file to see it listed.',
  'upload.library.more': 'Load more',
  'upload.uploading': 'Uploading',
  'upload.replace': 'Replace',
  'upload.rename': 'Rename',
  'upload.rename.title': 'Rename file',
  'upload.rename.placeholder': 'Filename',
  'upload.copy': 'Copy link',
  'upload.copied': 'Copied',
  'upload.select': 'Select',
  'upload.delete': 'Delete permanently',
  'upload.delete.confirm': 'Delete this file for everyone? Anything still using it will break.',
  'upload.filter.all': 'All',
  'upload.filter.image': 'Images',
  'upload.filter.video': 'Video',
  'upload.filter.document': 'Documents',
  'upload.error.tooLarge': 'That file is too large.',
  'upload.error.type': 'That file type is not accepted.',
  'upload.error.infected': 'That file failed the virus scan and was not stored.',
  'upload.error.scanUnavailable': 'The virus scanner is unreachable. Try again shortly.',
  'upload.error.notConfigured': 'File storage is not configured. An operator must set it up under Settings, Integrations.',
  'upload.error.generic': 'The upload failed.',
  'fileViewer.close': 'Close',
  'fileViewer.back': 'Back',
  'fileViewer.download': 'Download',
  'fileViewer.downloadOriginal': 'Download original',
  'fileViewer.print': 'Print',
  'fileViewer.share': 'Share',
  'fileViewer.copyLink': 'Copy link',
  'fileViewer.copyName': 'Copy file name',
  'fileViewer.copied': 'Copied',
  'fileViewer.copyFailed': 'Could not copy',
  'fileViewer.openExternally': 'Open externally',
  'fileViewer.refresh': 'Refresh',
  'fileViewer.refreshed': 'Reloaded. Nothing changed.',
  'fileViewer.refreshedNewer': 'A newer version replaced the one on screen.',
  'fileViewer.details': 'Details',
  'fileViewer.hideDetails': 'Hide details',
  'fileViewer.fullscreen': 'Fullscreen',
  'fileViewer.exitFullscreen': 'Exit fullscreen',
  'fileViewer.keyboardHelp': 'Keyboard shortcuts',
  'fileViewer.more': 'More',
  'fileViewer.previous': 'Previous file',
  'fileViewer.next': 'Next file',
  'fileViewer.position': '{index} of {count}',
  'fileViewer.local': 'Local',
  'fileViewer.remote': 'Remote',
  'fileViewer.readOnly': 'Read only',
  'fileViewer.retry': 'Retry',
  'fileViewer.cancel': 'Cancel',
  'fileViewer.loadedState': 'Loaded',
  'fileViewer.loadingState': 'Loading',
  'fileViewer.inspectingState': 'Inspecting source',
  'fileViewer.processingState': 'Processing',
  'fileViewer.errorState': 'Could not open',
  'fileViewer.progressOf': '{loaded} of {total}',
  'fileViewer.progressLoaded': '{loaded} loaded',
  'fileViewer.viewerTitle': 'File viewer',
  'fileViewer.untitled': 'Untitled file',
  'fileViewer.familyPdf': 'PDF',
  'fileViewer.familyImage': 'Image',
  'fileViewer.familyVideo': 'Video',
  'fileViewer.familyAudio': 'Audio',
  'fileViewer.familySpreadsheet': 'Spreadsheet',
  'fileViewer.familyPresentation': 'Presentation',
  'fileViewer.familyDocument': 'Document',
  'fileViewer.familyCode': 'Code',
  'fileViewer.familyText': 'Text',
  'fileViewer.familyArchive': 'Archive',
  'fileViewer.familyUnknown': 'File',
  'fileViewer.detailFilename': 'File name',
  'fileViewer.detailTitle': 'Title',
  'fileViewer.detailType': 'Type',
  'fileViewer.detailMime': 'MIME type',
  'fileViewer.detailSize': 'Size',
  'fileViewer.detailSource': 'Source',
  'fileViewer.detailHost': 'Host',
  'fileViewer.detailModified': 'Last modified',
  'fileViewer.detailEtag': 'ETag',
  'fileViewer.detailChecksum': 'Checksum (SHA-256)',
  'fileViewer.computeChecksum': 'Compute checksum',
  'fileViewer.computingChecksum': 'Computing…',
  'fileViewer.detailDimensions': 'Dimensions',
  'fileViewer.detailAspect': 'Aspect ratio',
  'fileViewer.detailDuration': 'Duration',
  'fileViewer.detailResolution': 'Resolution',
  'fileViewer.detailLines': 'Lines',
  'fileViewer.detailEncoding': 'Encoding',
  'fileViewer.detailLanguage': 'Language',
  'fileViewer.detailValidation': 'Validation',
  'fileViewer.detailFrames': 'Frames',
  'fileViewer.detailAlpha': 'Transparency',
  'fileViewer.detailRenderer': 'Rendered by',
  'fileViewer.rendererNative': 'This browser',
  'fileViewer.rendererConverted': 'Converted preview',
  'fileViewer.rendererNone': 'Not rendered',
  'fileViewer.yes': 'Yes',
  'fileViewer.no': 'No',
  'fileViewer.unknownValue': 'Unknown',
  'fileViewer.errorNetwork': 'The file could not be fetched. Check the connection and try again.',
  'fileViewer.errorCors': 'The file’s host does not allow this page to read it. Open it externally or download it instead.',
  'fileViewer.errorHttp': 'The server answered {status}.',
  'fileViewer.errorAuth': 'You are not allowed to read this file, or the sign-in has expired.',
  'fileViewer.errorNotFound': 'The file was not found. It may have been deleted or the link may be wrong.',
  'fileViewer.errorExpired': 'This link has expired. Ask for a fresh one.',
  'fileViewer.errorTooLarge': 'This file is {size}, above the {limit} this viewer will load. Download it instead.',
  'fileViewer.errorUnsupported': 'This browser cannot show this kind of file.',
  'fileViewer.errorDecode': 'The file could not be decoded. It may be corrupt.',
  'fileViewer.errorAborted': 'Loading was cancelled.',
  'fileViewer.mismatchWarning': 'The file’s contents do not match its declared type. It is shown as what the bytes say it is.',
  'fileViewer.unsupportedTitle': 'No preview for this file',
  'fileViewer.unsupportedSpreadsheet': 'Spreadsheets cannot be drawn by a browser on its own. Download the original to open it in a spreadsheet application.',
  'fileViewer.unsupportedPresentation': 'Presentations cannot be drawn by a browser on its own. Download the original to open it in a presentation application.',
  'fileViewer.unsupportedDocument': 'Word documents cannot be drawn by a browser on its own. Download the original to open it in a word processor.',
  'fileViewer.unsupportedArchive': 'Archives are not opened here. Download it to extract the files.',
  'fileViewer.unsupportedUnknown': 'This kind of file has no preview. The original is untouched and can be downloaded.',
  'fileViewer.unsupportedPdfInline': 'This browser has no built-in PDF viewer. Download the file or open it externally.',
  'fileViewer.unsupportedMediaCodec': 'This browser cannot decode this media. Download it or open it externally.',
  'fileViewer.unsupportedHls': 'This browser cannot play HLS streams without a player library, and none is installed here.',
  'fileViewer.convertedPreview': 'Converted text preview',
  'fileViewer.convertedPreviewHint': 'Text extracted on the server. Layout, images and formatting are not shown; the original is unchanged.',
  'fileViewer.loadConvertedPreview': 'Show text preview',
  'fileViewer.convertedPreviewFailed': 'The text preview could not be loaded.',
  'fileViewer.hexPreview': 'Hex preview',
  'fileViewer.hexPreviewHint': 'The first bytes of the file, for telling what it really is.',
  'fileViewer.showHexPreview': 'Show hex preview',
  'fileViewer.originalKept': 'Nothing here changes the original file.',
  'fileViewer.imageFit': 'Fit',
  'fileViewer.imageFill': 'Fill',
  'fileViewer.imageActual': '1:1',
  'fileViewer.zoomIn': 'Zoom in',
  'fileViewer.zoomOut': 'Zoom out',
  'fileViewer.rotateLeft': 'Rotate left',
  'fileViewer.rotateRight': 'Rotate right',
  'fileViewer.flipHorizontal': 'Flip horizontally',
  'fileViewer.flipVertical': 'Flip vertically',
  'fileViewer.resetView': 'Reset view',
  'fileViewer.checkerboard': 'Show transparency grid',
  'fileViewer.transformNote': 'View only. Rotating and flipping change nothing in the file.',
  'fileViewer.copyImage': 'Copy image',
  'fileViewer.exportPng': 'Export preview as PNG',
  'fileViewer.animatedNote': 'This format may be animated. The browser plays it as the file dictates.',
  'fileViewer.svgNote': 'Shown as a picture. Scripts and external resources inside the SVG never run.',
  'fileViewer.playbackSpeed': 'Speed',
  'fileViewer.loop': 'Loop',
  'fileViewer.pictureInPicture': 'Picture in picture',
  'fileViewer.fitContain': 'Fit',
  'fileViewer.fitCover': 'Fill',
  'fileViewer.fitOriginal': 'Original',
  'fileViewer.captureFrame': 'Capture frame',
  'fileViewer.quality': 'Quality',
  'fileViewer.qualityAuto': 'Auto',
  'fileViewer.live': 'Live',
  'fileViewer.bufferHealth': 'Buffer',
  'fileViewer.droppedFrames': 'Dropped frames',
  'fileViewer.bandwidth': 'Bandwidth',
  'fileViewer.protocol': 'Protocol',
  'fileViewer.hlsNative': 'HLS (built into this browser)',
  'fileViewer.hlsPlayer': 'HLS (player library)',
  'fileViewer.mediaDiagnostics': 'Diagnostics',
  'fileViewer.mediaPlaybackFailed': 'Playback failed. The browser could not decode this file.',
  'fileViewer.seekBack': 'Back 10 seconds',
  'fileViewer.seekForward': 'Forward 10 seconds',
  'fileViewer.playPause': 'Play or pause',
  'fileViewer.wrapLines': 'Wrap',
  'fileViewer.lineNumbers': 'Line numbers',
  'fileViewer.searchInFile': 'Search in file',
  'fileViewer.searchMatches': '{current} of {total}',
  'fileViewer.searchNone': 'No matches',
  'fileViewer.goToLine': 'Go to line',
  'fileViewer.language': 'Language',
  'fileViewer.viewRaw': 'Raw',
  'fileViewer.viewRendered': 'Preview',
  'fileViewer.viewGrid': 'Grid',
  'fileViewer.prettyPrint': 'Pretty print',
  'fileViewer.validJson': 'Valid JSON',
  'fileViewer.invalidJson': 'Invalid JSON at line {line}, column {column}: {message}',
  'fileViewer.copyCode': 'Copy contents',
  'fileViewer.truncatedNote': 'Showing the first {shown} of {total}. Download the file for the rest.',
  'fileViewer.sandboxNote': 'Rendered in a sandbox: no scripts, forms or outside requests.',
  'fileViewer.noRendererNote': 'No renderer is installed for this format, so it is shown as text.',
  'fileViewer.gridSummary': '{rows} rows, {columns} columns',
  'fileViewer.emptyText': 'This file is empty.',
  'fileViewer.pickerTitle': 'Open a file',
  'fileViewer.pickerFamilies': 'Supported file families',
  'fileViewer.pickerFromDevice': 'Open from device',
  'fileViewer.pickerDrop': 'Drop a file here',
  'fileViewer.pickerOr': 'or',
  'fileViewer.pickerChoose': 'Choose from device',
  'fileViewer.pickerFromLink': 'Open from link',
  'fileViewer.pickerUrl': 'File URL',
  'fileViewer.pickerUrlPlaceholder': 'https://',
  'fileViewer.pickerOpenLink': 'Open link',
  'fileViewer.pickerSendCredentials': 'Send credentials',
  'fileViewer.pickerSendCredentialsHint': 'Include this site’s cookies with the request. Only for links on a host that expects them.',
  'fileViewer.pickerRemember': 'Remember this link',
  'fileViewer.pickerRecentLocal': 'Recent local files',
  'fileViewer.pickerRecentLinks': 'Recent remote links',
  'fileViewer.pickerName': 'Name',
  'fileViewer.pickerSize': 'Size',
  'fileViewer.pickerModified': 'Modified',
  'fileViewer.pickerLink': 'Link',
  'fileViewer.pickerLastOpened': 'Last opened',
  'fileViewer.pickerLocalNote': 'Local files stay on this device unless you share them.',
  'fileViewer.pickerRemoteNote': 'The host of a link may refuse this page, or ask for a sign-in.',
  'fileViewer.pickerSizeNote': 'Files up to {size} open here. For larger files, use a link.',
  'fileViewer.pickerReopenNote': 'A browser cannot reopen a device file by itself. Choose it again to open it.',
  'fileViewer.pickerInvalidUrl': 'Enter a full https:// link.',
  'fileViewer.pickerNoRecent': 'Nothing yet.',
  'fileViewer.pickerClearRecent': 'Clear',
  'fileViewer.helpTitle': 'Keyboard shortcuts',
  'fileViewer.helpEscape': 'Close, or leave fullscreen',
  'fileViewer.helpArrows': 'Previous or next file',
  'fileViewer.helpZoom': 'Zoom in or out',
  'fileViewer.helpFit': 'Fit to view',
  'fileViewer.helpActual': 'Actual size',
  'fileViewer.helpRotate': 'Rotate clockwise',
  'fileViewer.helpSearch': 'Search',
  'fileViewer.helpFullscreen': 'Fullscreen',
  'fileViewer.helpDetails': 'Show or hide details',
  'fileViewer.helpSpace': 'Play or pause',
  'fileViewer.helpSeek': 'Seek back or forward',
  'upload.view': 'View',
  'imageCropper.title': 'Crop the picture',
  'imageCropper.instructions': 'Drag the picture to move it, and zoom with the slider, the mouse wheel or two fingers. With the keyboard, the arrow keys move it and the plus and minus keys zoom.',
  'imageCropper.stage': 'Picture position',
  'imageCropper.zoom': 'Zoom',
  'imageCropper.zoomIn': 'Zoom in',
  'imageCropper.zoomOut': 'Zoom out',
  'imageCropper.zoomValue': '{value}%',
  'imageCropper.preview': 'Preview',
  'imageCropper.previewAlt': 'How the finished picture looks',
  'imageCropper.output': 'Saved as a {width} × {height} {format}.',
  'imageCropper.fillNote': 'Space around the picture, and any see-through parts, are filled in with the background colour.',
  'imageCropper.reset': 'Reset',
  'imageCropper.cancel': 'Cancel',
  'imageCropper.confirm': 'Use this picture',
  'imageCropper.loading': 'Opening the picture…',
  'imageCropper.decodeError': 'This picture could not be opened. Try a JPG, PNG or WebP file.',
  'imageCropper.exportError': 'The picture could not be made. Try again, or choose another picture.',
  'imageCropper.lowResolution': 'The part you have chosen is only {edge} pixels across, less than {min}, so it will look blurry. Zoom out, or use a larger picture if you have one.'
};

export default files;
